import { Color, Spacing } from '@persona-id/design-tokens';
import Handlebars from 'handlebars';
import { get } from 'lodash/fp';
import { Fragment } from 'react';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import { LinkifyExternal } from 'dashboard/components/LinkifyExternal';
import AsyncImage from 'dashboard/components-lib/AsyncImage';
import DataDisplayValue from 'dashboard/modules/DataDisplay/DataDisplayValue';
import { ModuleDataDisplay } from 'dashboard/modules/DataDisplay/model';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { paragraphMixins } from 'design-system/mixins/text';
import { captureMessage } from 'lib/reporting';

const DescriptionContainer = styled('div')`
  ${paragraphMixins.lgBold}

  line-height: 2.5;
  border-bottom: 1px solid ${Color.CoreSlate50};
  grid-column: 1/-1;
`;

export interface DataDisplayProps {
  module: ModuleDataDisplay;
  data: Record<string, any>;
  isPreview: boolean;
}

const DataDisplay = ({ module, data, isPreview }: DataDisplayProps) => {
  const sections = module.sections.map((section, i) => {
    return (
      <Fragment key={`section-${i}`}>
        <DescriptionContainer>
          {interpolateHandlebars(section.title, data, isPreview)}
        </DescriptionContainer>
        <Spacer height={1.5} width={2.5} />
        <AttributeGrid>
          {section.attributes.map((attribute, i) => {
            const interpolatedLabel = interpolateHandlebars(attribute.label, data, isPreview);
            const obscurable = attribute.obscurable ?? false;

            // If the value in the config is something like `{{current_case.fields.field-key}}`
            // we can go straight to pulling the value from data without using handlebars interpolation.
            // This is important because handlebars interpolation of hashes and arrays returns
            // [object Object] instead of the value.
            // Longer term, we should probably resolve values for data display via platform resolver
            // and not even have an option for handlebars templated strings.
            let interpolatedValue: string;
            if (attribute.value.match(/^{{[^{}]*}}$/g)) {
              const path = attribute.value.slice(2, -2).split('.');
              const rawValue = get(path, data);
              interpolatedValue =
                rawValue == null || typeof rawValue == 'string'
                  ? rawValue
                  : JSON.stringify(rawValue);
            } else {
              interpolatedValue = interpolateHandlebars(attribute.value, data, isPreview);
            }

            // NB: interpolateHandlebars will return '' if the data does not include the passed in key.
            const collapseEmptyField = !interpolatedValue && module['collapse-empty-fields'];
            return (
              !collapseEmptyField && (
                <Attribute
                  key={`attribute-${i}-${obscurable ? 'obscurable' : 'unobscurable'}`}
                  label={interpolatedLabel}
                  value={interpolatedValue && <DataDisplayValue value={interpolatedValue} />}
                  placeholder='No data available'
                  toggleable={obscurable}
                  wordBreak='break-word'
                />
              )
            );
          })}
        </AttributeGrid>
        <Spacer height={1.5} width={2.5} />
      </Fragment>
    );
  });

  return (
    <Card data-test={`module__metadata-attributes--${module.token}`}>
      <Card.Body>
        <Accordion
          title={<TitleComponent module={module} data={data} isPreview={isPreview} />}
          menuItem={true}
          menuItemBorder={false}
          menuItemHover={false}
          padded={false}
          alignCaret='start'
        >
          {sections}
        </Accordion>
      </Card.Body>
    </Card>
  );
};

const TitleContainer = styled('div')`
  display: grid;
  grid-template-columns: auto auto;
  column-gap: ${Spacing[5]};
  align-items: top;
  justify-content: start;
`;

const Subtitle = styled('p')`
  ${paragraphMixins.md}
`;

interface TitleComponentProps {
  module: ModuleDataDisplay;
  data: Record<string, any>;
  isPreview: boolean;
}

const TitleComponent = ({ module, data, isPreview }: TitleComponentProps) => {
  const title = interpolateHandlebars(module.title, data, isPreview);
  const subtitle = module.subtitle
    ? interpolateHandlebars(module.subtitle, data, isPreview)
    : undefined;
  const subtitle2 = module['subtitle-2']
    ? interpolateHandlebars(module['subtitle-2'], data, isPreview)
    : undefined;
  const titleImageUrl = module['title-image-url']
    ? interpolateHandlebars(module['title-image-url'], data, isPreview)
    : undefined;

  return (
    <>
      <TitleContainer>
        {titleImageUrl && (
          <AsyncImage
            value={[{ url: titleImageUrl }]}
            width='96px'
            height='96px'
            allowPreviewZoom={false}
          />
        )}
        <div>
          <Card.Title>{title}</Card.Title>
          {subtitle && (
            <LinkifyExternal>
              <Subtitle>{subtitle}</Subtitle>
            </LinkifyExternal>
          )}
          {subtitle2 && (
            <LinkifyExternal>
              <Subtitle>{subtitle2}</Subtitle>
            </LinkifyExternal>
          )}
        </div>
      </TitleContainer>
      <Spacer height={1.5} width={2.5} />
    </>
  );
};

function interpolateHandlebars(
  templateString: string,
  data: Record<string, any>,
  isPreview: boolean,
) {
  if (isPreview) return templateString;

  try {
    const template = Handlebars.compile(templateString, { noEscape: true });
    return template(data);
  } catch (error) {
    captureMessage('Failed to parse value.', 'warning', {
      error,
      component: 'DataDisplay',
    });
    // NB: If Handlebars has trouble parsing the value, return a blank string. We don't want the page to crash just because one value can't be parsed.
    return '';
  }
}

export default DataDisplay;
